import {
  HttpErrorResponse,
  HttpInterceptorFn
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { PortalDestination } from './auth.models';
import { TokenRefreshService } from './token-refresh.service';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  if (isUnlockRequest(request.url)) {
    return next(request);
  }

  const auth = inject(AuthService);
  const refresh = inject(TokenRefreshService);
  const token = auth.getAccessToken();

  if (!token) {
    return next(request);
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`
  };

  const destination = resolveCurrentDestination();

  if (destination?.portalCode) {
    headers['X-DeskPlus-Portal'] = destination.portalCode;
  }

  if (destination?.tenantId) {
    headers['X-Tenant-Id'] = destination.tenantId;
  }

  const authenticatedRequest = request.clone({
    setHeaders: headers
  });

  return next(authenticatedRequest).pipe(
    catchError((error: unknown) => {
      if (
        !(error instanceof HttpErrorResponse) ||
        error.status !== 401 ||
        isRefreshRequest(request.url)
      ) {
        return throwError(() => error);
      }

      return refresh.refreshAccessToken().pipe(
        switchMap((newToken) =>
          next(
            authenticatedRequest.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`
              }
            })
          )
        ),
        catchError((refreshError) => {
          auth.clearLocalSession();
          return throwError(() => refreshError);
        })
      );
    })
  );
};

function isRefreshRequest(url: string): boolean {
  return url.toLowerCase().includes('/api/auth/refresh');
}

function resolveCurrentDestination(): PortalDestination | null {
  try {
    const raw = sessionStorage.getItem(
      'deskplus.authorized_destinations'
    );

    if (!raw) {
      return null;
    }

    const destinations = JSON.parse(raw) as PortalDestination[];
    const path = window.location.pathname.toLowerCase();

    return (
      destinations.find((destination) => {
        const route = destination.routePath?.toLowerCase();
        return !!route && (
          path === route ||
          path.startsWith(`${route}/`)
        );
      }) ?? null
    );
  } catch {
    return null;
  }
}


function isUnlockRequest(url: string): boolean {
  return url.toLowerCase().includes('/api/auth/unlock');
}
